import { DEFAULT_AMENDMENT_REASON_LABELS } from "@/lib/pharmacyFirst";
import type { AmendmentReason } from "@/lib/pharmacyFirst";

const FIELD_LABELS: Record<string, string> = {
  ageYears: "Age (years)",
  dateOfBirth: "Date of Birth",
  isFemale: "Sex",
  isPregnant: "Pregnant",
  isBreastfeeding: "Breastfeeding",
  weightKg: "Weight (kg)",
  heightCm: "Height (cm)",
  bmi: "BMI",
  hasPenicillinAllergy: "Penicillin Allergy",
  knownAllergies: "Known Allergies",
  currentMedications: "Current Medications",
  biologicalSex: "Biological Sex",
  ethnicAdjustmentGroup: "Ethnicity",
  waistCircumferenceCm: "Waist Circumference (cm)",
};

function formatFieldName(field: string): string {
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  return field
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null) return "Not provided";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return value || "Not provided";
  if (typeof value === "number") return String(value);
  if (Array.isArray(value)) {
    if (value.length === 0) return "None";
    return value
      .map((v: any) =>
        typeof v === "object" && v?.substance ? v.substance : String(v)
      )
      .join(", ");
  }
  return String(value);
}

function formatDate(iso: string | undefined): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatOutcomeType(type: string | undefined): string {
  if (!type) return "Unknown";
  const map: Record<string, string> = {
    supply: "Supply",
    self_care: "Self-Care",
    refer_urgent: "Urgent Referral",
    refer_routine: "Routine Referral",
    emergency: "Emergency",
    cannot_supply: "Cannot Supply",
  };
  return map[type] || type.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

export function isPathwayAuditJson(text: string): boolean {
  if (!text || !text.trim().startsWith("{")) return false;
  try {
    const obj = JSON.parse(text);
    return !!(obj.consultationId && obj.pathwayId && obj.answers);
  } catch {
    return false;
  }
}

export function formatAuditForPdf(jsonText: string): string {
  const audit = JSON.parse(jsonText);
  const lines: string[] = [];

  lines.push(`**Pathway:** ${audit.pathwayName || audit.pathwayId || "Unknown"}`);
  if (audit.startedAt) lines.push(`**Started:** ${formatDate(audit.startedAt)}`);
  if (audit.completedAt) lines.push(`**Completed:** ${formatDate(audit.completedAt)}`);
  lines.push("");

  if (audit.patient) {
    lines.push("**Patient Details:**");
    if (audit.patient.dateOfBirth) lines.push(`- Date of Birth: ${audit.patient.dateOfBirth}`);
    if (audit.patient.ageYears !== undefined) lines.push(`- Age: ${audit.patient.ageYears} years`);
    if (audit.patient.isFemale !== undefined) lines.push(`- Sex: ${audit.patient.isFemale ? "Female" : "Male"}`);
    if (audit.patient.knownAllergies) lines.push(`- Known Allergies: ${formatValue(audit.patient.knownAllergies)}`);
    if (audit.patient.currentMedications) lines.push(`- Current Medications: ${formatValue(audit.patient.currentMedications)}`);
    lines.push("");
  }

  if (audit.consentObtained !== undefined) {
    lines.push(`**Consent:** ${audit.consentObtained ? "Obtained" : "Not obtained"}`);
    lines.push("");
  }

  if (audit.amendments && audit.amendments.length > 0) {
    lines.push("**Patient Information Amendments:**");
    for (const a of audit.amendments) {
      const reasonLabel =
        DEFAULT_AMENDMENT_REASON_LABELS[a.reason as AmendmentReason] || a.reason;
      lines.push(`- ${formatFieldName(a.field)}: ${formatValue(a.originalValue)} → ${formatValue(a.newValue)} (${reasonLabel})`);
    }
    lines.push("");
  }

  if (audit.finalOutcome) {
    const outcome = audit.finalOutcome;
    lines.push("**Outcome:**");
    lines.push(`- Type: ${formatOutcomeType(outcome.outcomeType)}`);
    if (outcome.title) lines.push(`- ${outcome.title}`);
    if (outcome.reason) lines.push(`- ${outcome.reason}`);
    if (outcome.safetyNetting && outcome.safetyNetting.length > 0) {
      lines.push("- Safety Netting:");
      for (const s of outcome.safetyNetting) {
        lines.push(`  - ${s}`);
      }
    }
    lines.push("");
  }

  if (audit.supplyDetails) {
    const sd = audit.supplyDetails;
    lines.push("**Prescription Details:**");
    if (sd.drugId) lines.push(`- Drug: ${sd.drugId.charAt(0).toUpperCase() + sd.drugId.slice(1)}`);
    // Combination products already resolve to a formatted label, so only a bare
    // number needs the unit appending.
    if (sd.resolvedDoseMg)
      lines.push(
        typeof sd.resolvedDoseMg === "number"
          ? `- Dose: ${sd.resolvedDoseMg}mg`
          : `- Dose: ${sd.resolvedDoseMg}`
      );
    if (sd.quantity?.label) lines.push(`- Quantity: ${sd.quantity.label}`);
    if (sd.totalDurationDays) lines.push(`- Duration: ${sd.totalDurationDays} days`);
    if (sd.instructionText) lines.push(`- Instructions: ${sd.instructionText}`);
    lines.push("");
  }

  if (audit.paymentTriggered) {
    lines.push("**Payment:** Claimable");
    if (audit.paymentTriggeredAt) lines.push(`- Triggered at: ${formatDate(audit.paymentTriggeredAt)}`);
    lines.push("");
  }

  if (audit.contextSnapshot) {
    const snap = audit.contextSnapshot;
    const contextLines: string[] = [];
    if (snap.feverPainScore !== undefined) contextLines.push(`- FeverPAIN Score: ${snap.feverPainScore}/5`);
    if (snap.isPregnant !== undefined) contextLines.push(`- Pregnant: ${snap.isPregnant ? "Yes" : "No"}`);
    if (snap.isFollowUp !== undefined) contextLines.push(`- Follow-up: ${snap.isFollowUp ? "Yes" : "No"}`);
    if (contextLines.length > 0) {
      lines.push("**Clinical Context:**");
      lines.push(...contextLines);
      lines.push("");
    }
  }

  return lines.join("\n").trim();
}
